/**
 * INFDOC-3a · Textos de las incidencias del documento, comunes al panel de P1 y
 * a los informes (PDF del ayuntamiento en INFDOC-3b, anexo del GREA en INFDOC-3c).
 *
 * Una sola fuente de textos: lo que dice el panel y lo que dirán los PDF no puede
 * divergir. Sacado de `Step1Page.tsx` (INFDOC-2a/2b) sin cambiar ningún texto,
 * salvo el de «documento lejos» (DOP-1 e de DOCLEJ; DOP-2 b y DOP-3a-2 a de
 * INFDOC-3, Luis, 3/4-nov-2026): ya no habla de «dos fuentes que coinciden»
 * cuando una es copia de la otra; dice qué fuente sitúa el elemento (o su
 * dirección) y a cuántos metros del documento.
 */

import type { CodigoIncidencia } from './documentIncidents';

export const plural = (n: number, uno: string, varios: string) => (n === 1 ? uno : varios);

export const mayuscula = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** 249 → «249 m»; 3698 → «3,7 km»; 2032 → «2 km» (coma decimal, RAE) */
export const distancia = (m: number) =>
  (m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1).replace(/\.0$/, '').replace('.', ',')} km`);

/** [7] → «7»; [7, 18] → «7 y 18»; [7, 9, 18] → «7, 9 y 18» */
export const enumerar = (ns: readonly (number | string)[]) =>
  (ns.length < 2 ? ns.join('') : `${ns.slice(0, -1).join(', ')} y ${ns.at(-1)}`);

/**
 * Texto llano de cada tipo, sin la cifra (va en su columna; propuesta B de Luis,
 * 3-nov-2026). DOP-7 de INFDOC-0: nada de códigos a la vista. `n` = filas.
 */
export const TEXTO_INCIDENCIA: Record<CodigoIncidencia, (n: number) => string> = {
  sin_coordenada: () => 'sin coordenada',
  sin_coordenada_ni_direccion: () => 'sin coordenada ni dirección',
  coordenada_incompleta: () => 'con la coordenada incompleta (falta X o Y)',
  coordenada_mal_escrita: () => 'con la coordenada mal escrita',
  rotulo_de_plantilla_en_coordenada: () => 'con un rótulo de plantilla en lugar de la coordenada',
  fuera_del_termino_mas_de_500_m: () => 'fuera del término, a más de 500 m',
  fuera_del_termino_hasta_500_m: () => 'fuera del término, a 500 m o menos',
  nombre_generico: () => 'con nombre genérico',
  siglas_solas: () => 'con solo unas siglas por nombre',
  texto_mal_codificado: () => 'con letras mal leídas',
  palabras_pegadas: () => 'con palabras pegadas en el nombre',
  coordenada_compartida: () => 'en la misma posición que otra fila',
  sin_coordenada_y_sin_localizar: () => 'sin coordenada y sin situar',
  // DOP-3a-2 (a) de INFDOC-3: la línea del resumen; la frase de cada fila, `textoDocumentoLejos`
  documento_lejos_de_fuentes_que_coinciden: () => 'lejos de su dirección o de donde lo sitúan las fuentes',
  sistema_de_referencia_antiguo_inferido: () => 'con indicios de sistema antiguo (ED50)',
  fila_repetida: n => `${plural(n, 'fila repetida', 'filas repetidas')} (mismo nombre y posición)`,
  documento_sin_municipio: () => 'el documento no indica el municipio',
};

// ─── «Documento lejos»: qué fuente lo sitúa (DOP-2 b y DOP-3a-2 a de INFDOC-3) ───

/** Qué sitúa la fuente: la dirección que trae el plan (callejero) o el elemento por su nombre */
export type QueSitua = 'direccion' | 'elemento';

export interface FuenteLlana {
  /** Nombre para leer, con su artículo: «el Callejero de Andalucía», «OpenStreetMap» */
  nombre: string;
  situa: QueSitua;
}

/**
 * Nombres llanos por familia (tabla sellada en DOP-3a-2). Las copias llevan el
 * nombre de su original (TSF lee DERA de `LocalDataService`; DOCLEJ §2), así que
 * se nombran una sola vez. Orden: la primera regla que casa.
 */
export const FUENTES_LLANAS: ReadonlyArray<readonly [RegExp, FuenteLlana]> = [
  [/^cdau(_r2)?$/, { nombre: 'el Callejero de Andalucía', situa: 'direccion' }],
  [/^cartociudad$/, { nombre: 'CartoCiudad', situa: 'direccion' }],
  [/^nominatim$/, { nombre: 'OpenStreetMap', situa: 'direccion' }],
  [/^(local_dera_|tsf_|dera_|wfs_dera$)/, { nombre: 'el inventario de la Junta (DERA)', situa: 'elemento' }],
  [/^(iaph|obph|cgpha)_r2$/, { nombre: 'el patrimonio de la Junta (IAPH)', situa: 'elemento' }],
  [/^(nga_r2|nga_local|nomenclator|disambiguated_local)$/, { nombre: 'el Nomenclátor de Andalucía', situa: 'elemento' }],
  [/^catastro_/, { nombre: 'el Catastro', situa: 'elemento' }],
  [/^edu_centers_r2$/, { nombre: 'el directorio de centros educativos de la Junta', situa: 'elemento' }],
];

/** Fuera de la tabla: nombre genérico; el identificador va al anexo del GREA (INFDOC-3c) */
export const FUENTE_DESCONOCIDA: FuenteLlana = { nombre: 'otra fuente pública', situa: 'elemento' };

const CALLEJERO = 'el Callejero de Andalucía';
const CARTOCIUDAD = 'CartoCiudad';

export function fuenteLlana(id: string): FuenteLlana {
  const clave = id.trim().toLowerCase();
  return FUENTES_LLANAS.find(([re]) => re.test(clave))?.[1] ?? FUENTE_DESCONOCIDA;
}

/**
 * Quién sitúa el elemento: nombres sin repetir (una copia no cuenta dos veces) y
 * CartoCiudad fuera cuando va con el Callejero de Andalucía, del que toma en
 * Andalucía sus direcciones (DOCLEJ §2: 153 de 223 pares, a menos de 1 m).
 * Con alguna fuente de elemento, la frase es la del elemento (par mixto incluido).
 */
export function quienSitua(fuentes: readonly string[]): { nombres: string[]; situa: QueSitua } {
  const llanas = fuentes.map(fuenteLlana);
  let nombres = [...new Set(llanas.map(f => f.nombre))];
  if (nombres.includes(CALLEJERO)) nombres = nombres.filter(n => n !== CARTOCIUDAD);
  const situa: QueSitua = llanas.some(f => f.situa === 'elemento') ? 'elemento' : 'direccion';
  return { nombres, situa };
}

/**
 * Frase de una fila con «documento lejos» (DOP-3a-2 a: la frase entera, sin apunte):
 * - «A 652 m de su dirección en el Callejero de Andalucía»
 * - «A 550 m de su dirección según CartoCiudad y OpenStreetMap»
 * - «A 881 m de donde lo sitúa el inventario de la Junta (DERA)»
 * - «A N m de donde lo sitúan el Callejero de Andalucía y el inventario de la Junta (DERA)»
 * `metros` = distancia del documento al punto de las fuentes (`distanciaMetros` del detector).
 */
export function textoDocumentoLejos(metros: number, fuentes: readonly string[]): string {
  const { nombres, situa } = quienSitua(fuentes);
  const lista = enumerar(nombres);
  const d = distancia(metros);
  if (situa === 'elemento') {
    return `A ${d} de donde ${plural(nombres.length, 'lo sitúa', 'lo sitúan')} ${lista}`;
  }
  return nombres.length === 1 && nombres[0] === CALLEJERO
    ? `A ${d} de su dirección en ${CALLEJERO}`
    : `A ${d} de su dirección según ${lista}`;
}
